/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, Popover, Tooltip, Skeleton,
} from '@mui/material';
import StatusBadge from 'Shared/UI/StatusBadge';
import Toast from '../../Shared/UI/Toast';
import iconEye from '../../Images/Icon_eye.svg';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DropdownSelect from '../../Shared/UI/DropdownSelect';
import DateRangePicker from '../../Shared/UI/DateRangePicker';
import { formatAmount } from 'Utils/ulilities';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useDebounce from 'hooks/useDebounce';
import { getExpeditions } from '../../Services';
import ConfirmReceptionDialog from './ConfirmReceptionDialog';
import { searchCommandes, confirmDeliveryBulk, confirmSalesDelivery, confirmSalesDeliveryBulk } from './OrdersServices';

const STATUT_OPTIONS = [
  { value: 'envoyee', label: 'Envoyée' },
  { value: 'en-cours', label: 'En cours de traitement' },
  { value: 'traite', label: 'Traitée' },
];

const PageSizeSelector = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const sizes = [5, 10, 20, 50];
  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', userSelect: 'none', backgroundColor: '#FFFFFF', borderRadius: '6px', px: '6px', py: '2px' }}
      >
        <ChevronLeftIcon sx={{ fontSize: 14, color: '#385576', transform: 'rotate(-90deg)' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#385576', fontFamily: "'Open Sans', sans-serif" }}>
          {value}
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0px 4px 16px rgba(0,0,0,0.08)', border: '1px solid #F1F5F9', mt: '4px', minWidth: '60px' },
        }}
      >
        <Box sx={{ p: '6px' }}>
          {sizes.map((n, i) => (
            <Box
              key={n}
              onClick={() => { onChange(n); setAnchor(null); }}
              sx={{ px: '12px', py: '8px', cursor: 'pointer', borderRadius: '6px', borderBottom: i < sizes.length - 1 ? '1px solid #F1F5F9' : 'none', '&:hover': { backgroundColor: '#F8FAFC' } }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: n === value ? 600 : 400, color: n === value ? '#007BC6' : '#385576', fontFamily: "'Open Sans', sans-serif" }}>
                {n}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

const TH = ({ children, width, sortKey, onSort, activeSortKey, sortDir }) => {
  const isActive = sortKey && activeSortKey === sortKey;
  return (
    <TableCell
      sx={{ fontWeight: 600, fontSize: '14px', color: '#385576', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid #DEE5EE', py: '8px', px: '20px', width, whiteSpace: 'nowrap', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none', backgroundColor: '#fff', '&:last-of-type': { borderTopRightRadius: '9px' } }}
      onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
        {children}
        {sortKey && (
          isActive && sortDir === 'asc'
            ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />
        )}
      </Box>
    </TableCell>
  );
};

const TD = ({ children }) => (
  <TableCell sx={{ fontSize: '14px', color: '#000000', fontWeight: 600, borderBottom: 'none', py: '10px', px: '20px', whiteSpace: 'nowrap' }}>
    {children}
  </TableCell>
);

const parseUrlParams = (search) => {
  const p = new URLSearchParams(search);
  const toDate = (str) => { if (!str) return null; const d = new Date(str); return isNaN(d) ? null : d; };
  return {
    search: p.get('q') || '',
    statut: p.getAll('statut'),
    transport: p.getAll('transport'),
    origin: p.getAll('origin'),
    dateRange: { start: toDate(p.get('dateStart')), end: toDate(p.get('dateEnd')) },
    page: parseInt(p.get('page') || '1', 10),
    size: parseInt(p.get('size') || '10', 10),
    sortField: p.get('sort') || 'created_at',
    sortDir: p.get('dir') || 'desc',
  };
};

/* ── Main component ───────────────────────────────────────────────── */
const Orders = () => {
  const { callApi } = useNetworkLoader();
  const history = useHistory();
  const location = useLocation();

  const initial = parseUrlParams(location.search);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [search, setSearch] = useState(initial.search);
  const [statut, setStatut] = useState(initial.statut);
  const [transport, setTransport] = useState(initial.transport);
  const [origin, setOrigin] = useState(initial.origin);
  const [dateRange, setDateRange] = useState(initial.dateRange);
  const [sortField, setSortField] = useState(initial.sortField);
  const [sortDir, setSortDir] = useState(initial.sortDir);

  const debouncedSearch = useDebounce(search, 350);
  const totalPages = Math.ceil(total / size);

  /* Expeditions for transport dropdown */
  const [expeditions, setExpeditions] = useState([]);
  useEffect(() => {
    callApi(() => getExpeditions(), (data) => setExpeditions(data || []), false);
  }, []);
  const transportOptions = expeditions.map((e) => ({ value: String(e.id), label: e.name }));

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  /* Load commandes — server-side */
  const loadCommandes = () => {
    setLoading(true);
    const formatLocalDate = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const startDate = dateRange.start ? formatLocalDate(dateRange.start) : '';
    const endDate = dateRange.end ? formatLocalDate(dateRange.end) : '';
    callApi(
      () => searchCommandes({
        page: page - 1,
        size,
        sort: sortField ? `${sortField},${sortDir}` : 'date,desc',
        search: debouncedSearch,
        expeditionIds: transport.length > 0 ? transport.map(Number) : null,
        statuts: statut.length > 0 ? statut : null,
        origins: origin.length > 0 ? origin : null,
        startDate,
        endDate,
      }),
      (data) => {
        setOrders(data?.content || []);
        setTotal(data?.totalElements || 0);
        setLoading(false);
      },
      false,
    );
  };

  useEffect(() => {
    loadCommandes();
  }, [page, size, sortField, sortDir, debouncedSearch, statut, transport, origin, dateRange]);

  useEffect(() => {
    const formatLocalDate = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const p = new URLSearchParams();
    if (debouncedSearch) p.set('q', debouncedSearch);
    statut.forEach((s) => p.append('statut', s));
    transport.forEach((t) => p.append('transport', t));
    origin.forEach((o) => p.append('origin', o));
    if (dateRange.start) p.set('dateStart', formatLocalDate(dateRange.start));
    if (dateRange.end) p.set('dateEnd', formatLocalDate(dateRange.end));
    if (page !== 1) p.set('page', page);
    if (size !== 10) p.set('size', size);
    if (sortField !== 'created_at') p.set('sort', sortField);
    if (sortDir !== 'desc') p.set('dir', sortDir);
    history.replace({ search: p.toString() });
  }, [debouncedSearch, statut, transport, origin, dateRange, page, size, sortField, sortDir]);

  const handleReset = () => {
    setSearch(''); setStatut([]); setTransport([]); setOrigin([]); setDateRange({ start: null, end: null }); setPage(1);
  };

  const isTraitee = (order) => order.statut === 'Traitée';
  const isConfirmed = (order) => order.deliveryConfirmedAt != null;

  const selectionKey = (o) => (o.origin === 'SALES' ? -(o.opsOrderId) : o.id);
  const findByKey = (key) => (key < 0
    ? orders.find((o) => o.origin === 'SALES' && o.opsOrderId === -key)
    : orders.find((o) => o.id === key));

  const selectedTraiteeCount = selected.filter((key) => {
    const o = findByKey(key);
    return o && isTraitee(o) && !isConfirmed(o);
  }).length;

  const handleConfirmReception = () => {
    const validKeys = selected.filter((key) => {
      const o = findByKey(key);
      return o && isTraitee(o) && !isConfirmed(o);
    });
    const edgeIds = validKeys.filter((k) => k > 0);
    const salesOpsIds = validKeys.filter((k) => k < 0).map((k) => -k);
    const total = validKeys.length;
    const calls = [];
    if (edgeIds.length > 0) calls.push(confirmDeliveryBulk(edgeIds));
    if (salesOpsIds.length > 0) calls.push(confirmSalesDeliveryBulk(salesOpsIds));
    setConfirmLoading(true);
    callApi(
      () => Promise.all(calls),
      () => {
        setConfirmLoading(false);
        setSelected([]);
        setDialogOpen(false);
        setToast({ open: true, message: `Réception confirmée pour ${total} commande${total > 1 ? 's' : ''} avec succès`, severity: 'success' });
        loadCommandes();
      },
      false,
      () => setConfirmLoading(false),
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(orders.filter((o) => isTraitee(o) && !isConfirmed(o)).map(selectionKey));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (key) => {
    const order = findByKey(key);
    const orderIsTraitee = order && isTraitee(order) && !isConfirmed(order);
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      if (orderIsTraitee) {
        const onlyTraitee = prev.filter((r) => {
          const o = findByKey(r);
          return o && isTraitee(o) && !isConfirmed(o);
        });
        return [...onlyTraitee, key];
      }
      const hasTraitee = prev.some((r) => {
        const o = findByKey(r);
        return o && isTraitee(o);
      });
      if (hasTraitee) return prev;
      return [...prev, key];
    });
  };

  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(page * size, total);

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px', fontFamily: "'Open Sans', sans-serif", '& *': { fontFamily: "'Open Sans', sans-serif" } }}>

      {/* Header */}
      <Box sx={{ mb: '16px', mt: '8px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
            Mes commandes
            <Box component="span" sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', ml: '4px' }}>
              {loading ? <>(<Skeleton component="span" variant="text" width={24} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />)</> : `(${total})`}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#385576', fontWeight: 600 }}>
            Gérez et suivez l'état de vos commandes
          </Typography>
        </Box>
        {selectedTraiteeCount > 0 && (
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ backgroundColor: '#0AD69F', textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: '20px', height: '42px', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#08b886' } }}
          >
            Confirmer la réception ({selectedTraiteeCount})
          </Button>
        )}
      </Box>

      {/* Filter bar */}
      <Box sx={{ position: 'sticky', top: '62px', zIndex: 10, backgroundColor: '#F8FAFC', pt: '8px', pb: '8px', mb: '10px' }}>
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #DEE5EE', borderRadius: '10px', p: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ flex: 3, minWidth: 0, height: '45px', display: 'flex', alignItems: 'center', border: '1px solid #DEE5EE', borderRadius: '8px', px: '12px', gap: '8px', backgroundColor: '#F8FAFC' }}>
          <SearchOutlinedIcon sx={{ fontSize: 20, color: '#8397AE', flexShrink: 0 }} />
          <input
            placeholder="Rechercher par N° commande ou montant..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#1A2035', width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 2, minWidth: 0, '& > *': { width: '100% !important', flexShrink: '1 !important' } }}>
          <DropdownSelect options={transportOptions} value={transport} onChange={(val) => { setTransport(val); setPage(1); }} placeholder="Transport" width="100%" multiple />
        </Box>
        <Box sx={{ flex: 2, minWidth: 0, '& > *': { width: '100% !important', flexShrink: '1 !important' } }}>
          <DropdownSelect options={STATUT_OPTIONS} value={statut} onChange={(val) => { setStatut(val); setPage(1); }} placeholder="Statut" width="100%" multiple />
        </Box>
        <Box sx={{ flex: 1.5, minWidth: 0, '& > *': { width: '100% !important', flexShrink: '1 !important' } }}>
          <DropdownSelect options={[{ value: 'EDGE', label: 'EDGE' }, { value: 'SALES', label: 'SALES' }]} value={origin} onChange={(val) => { setOrigin(val); setPage(1); }} placeholder="Origine" width="100%" multiple />
        </Box>
        <Box sx={{ flex: 2, minWidth: 0, '& > *': { width: '100% !important', flexShrink: '1 !important' } }}>
          <DateRangePicker placeholder="Date de commande" value={dateRange} onApply={(start, end) => { setDateRange({ start, end }); setPage(1); }} width="100%" />
        </Box>
        <Button onClick={handleReset} variant="contained"
          sx={{ height: '45px', minWidth: '130px', px: '20px', borderRadius: '8px', backgroundColor: '#007BC6', textTransform: 'none', fontWeight: 600, fontSize: '13px', flexShrink: 0, '&:hover': { backgroundColor: '#005F9E' } }}>
          Réinitialiser
        </Button>
      </Box>
      </Box>

      {/* Table */}
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #DEE5EE', borderRadius: '10px', overflow: 'visible' }}>
        <TableContainer sx={{ borderRadius: '10px', overflow: 'visible' }}>
          <Table stickyHeader sx={{ '& .MuiTableCell-stickyHeader': { top: '135px', backgroundColor: '#fff' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: '8px', px: '12px', borderBottom: '1px solid #DEE5EE', width: 220, backgroundColor: '#fff', borderTopLeftRadius: '9px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox size="small"
                      checked={(() => {
                        if (orders.length === 0) return false;
                        const checkable = orders.filter((o) => isTraitee(o) && !isConfirmed(o));
                        return checkable.length > 0 && checkable.every((o) => selected.includes(selectionKey(o)));
                      })()}
                      indeterminate={(() => {
                        const checkable = orders.filter((o) => isTraitee(o) && !isConfirmed(o));
                        const checkedCount = checkable.filter((o) => selected.includes(selectionKey(o))).length;
                        return checkedCount > 0 && checkedCount < checkable.length;
                      })()}
                      onChange={handleSelectAll}
                      sx={{ color: '#C2D2DF', '&.Mui-checked': { color: '#007BC6' }, '&.Mui-disabled': { opacity: 0.3, color: '#DEE5EE' }, p: '2px', flexShrink: 0 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#385576', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        N° Commande
                      </Typography>
                      {sortField === 'id' && sortDir === 'asc'
                        ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: '#385576' }} />
                        : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: sortField === 'id' ? '#385576' : '#C0C8D4' }} />}
                    </Box>
                  </Box>
                </TableCell>
                <TH width={100}>Origine</TH>
                <TH width={200} sortKey="created_at" onSort={handleSort} activeSortKey={sortField} sortDir={sortDir}>Date</TH>
                <TH width={160} sortKey="expedition_id" onSort={handleSort} activeSortKey={sortField} sortDir={sortDir}>Transport</TH>
                <TH width={140} sortKey="montant" onSort={handleSort} activeSortKey={sortField} sortDir={sortDir}>Montant</TH>
                <TH width={220} sortKey="statut" onSort={handleSort} activeSortKey={sortField} sortDir={sortDir}>Statut</TH>
                <TH width={100}>Action</TH>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(size)].map((_, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: '4px', flexShrink: 0 }} />
                        <Skeleton variant="text" width={120} height={22} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="rectangular" width={56} height={22} sx={{ borderRadius: '20px' }} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="text" width={90} height={22} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="text" width={80} height={22} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="text" width={70} height={22} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="rectangular" width={130} height={28} sx={{ borderRadius: '20px' }} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}><Skeleton variant="circular" width={20} height={20} /></TableCell>
                  </TableRow>
                ))
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: '60px', color: '#8F8F8F', fontSize: '14px' }}>
                    Aucune commande trouvée
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order, idx) => {
                  const confirmed = isConfirmed(order);
                  const isSales = order.origin === 'SALES';
                  const rowKey = isSales ? `sales-${order.opsOrderId}` : order.id;
                  const bgColor = idx % 2 === 0 ? '#fff' : '#F8FAFC';
                  return (
                    <TableRow key={rowKey}
                      sx={{ backgroundColor: bgColor, transition: 'background-color 0.15s' }}>

                      <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        {confirmed && (
                          <Box sx={{ position: 'absolute', left: 0, top: '6px', bottom: '6px', width: '3px', backgroundColor: '#0AD69F', borderRadius: '0 2px 2px 0' }} />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Checkbox size="small"
                            checked={selected.includes(selectionKey(order))}
                            disabled={confirmed || !isTraitee(order)}
                            onChange={() => handleSelectOne(selectionKey(order))}
                            sx={{ color: '#C2D2DF', '&.Mui-checked': { color: '#007BC6' }, '&.Mui-disabled': { opacity: 0.3, color: '#DEE5EE' }, p: '2px', flexShrink: 0 }}
                          />
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000', whiteSpace: 'nowrap' }}>
                            {order.numeroCommande || '—'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: order.origin === 'SALES' ? '55px' : '52px', height: '29px',
                          borderRadius: '8px',
                          backgroundColor: order.origin === 'SALES' ? 'rgba(15,186,129,0.06)' : '#027BC617',
                          color: order.origin === 'SALES' ? '#0FBA81' : '#007BC6',
                          fontSize: '12px', fontWeight: 700, textTransform: 'capitalize',
                        }}>
                          {order.origin === 'SALES' ? 'Sales' : 'Edge'}
                        </Box>
                      </TableCell>
                      <TD>{order.date ? new Date(order.date).toLocaleDateString('fr-FR') : '—'}</TD>
                      <TD>{order.transport || '—'}</TD>
                      <TD>{formatAmount(order.montant || 0)} Dh</TD>

                      <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}>
                        <StatusBadge status={order.statut} />
                      </TableCell>

                      <TableCell sx={{ py: '10px', px: '20px', borderBottom: 'none' }}>
                        <Tooltip title="Voir le détail" placement="top" arrow>
                          <Box
                            onClick={(e) => { e.stopPropagation(); history.push(isSales ? `/orders/sales/${order.opsOrderId}` : `/orders/${order.id}`); }}
                            sx={{ display: 'inline-flex', cursor: 'pointer', '&:hover img': { filter: 'brightness(0) saturate(100%) invert(29%) sepia(89%) saturate(876%) hue-rotate(182deg) brightness(100%) contrast(101%)', transition: 'filter 0.15s' }, '& img': { transition: 'filter 0.15s' } }}
                          >
                            <img src={iconEye} alt="view" style={{ width: 20, height: 24, objectFit: 'contain' }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {(() => {
          const GROUP_SIZE = 3;
          const groupIndex = Math.floor((page - 1) / GROUP_SIZE);
          const groupStart = groupIndex * GROUP_SIZE + 1;
          const groupEnd = Math.min(groupStart + GROUP_SIZE - 1, totalPages);
          const hasMore = groupEnd < totalPages;
          const pageNums = [];
          for (let i = groupStart; i <= groupEnd; i++) pageNums.push(i);
          const btnSx = (active) => ({ minWidth: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: 'none', backgroundColor: active ? '#007BC6' : 'transparent', color: active ? '#fff' : '#385576', fontSize: '13px', fontWeight: active ? 700 : 400, fontFamily: "'Open Sans', sans-serif", cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: active ? '#005F9E' : '#F0F0F0' } });
          const arrowSx = (disabled) => ({ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', color: disabled ? '#C5C5C5' : '#385576', cursor: disabled ? 'default' : 'pointer', '&:hover': { backgroundColor: disabled ? 'transparent' : '#F0F0F0' } });
          const lastRowBg = orders.length === 0 ? '#fff' : (orders.length - 1) % 2 === 0 ? '#fff' : '#F8FAFC';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', borderTop: '1px solid #DEE5EE', gap: '5px', px: '20px', py: '8px', backgroundColor: lastRowBg }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#8F8F8F', fontFamily: "'Open Sans', sans-serif", whiteSpace: 'nowrap' }}>
                    Nombre de lignes par page
                  </Typography>
                  <PageSizeSelector value={size} onChange={(val) => { setSize(val); setPage(1); }} />
                </Box>
                <Typography sx={{ fontSize: '13px', color: '#8F8F8F', fontFamily: "'Open Sans', sans-serif" }}>
                  {total === 0 ? 'Aucun résultat' : `Affichage de ${from} à ${to} sur ${total} résultats`}
                </Typography>
              </Box>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box sx={arrowSx(page === 1)} onClick={() => page > 1 && setPage(page - 1)}>
                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  </Box>
                  {pageNums.map((p) => (
                    <Box key={p} sx={btnSx(p === page)} onClick={() => setPage(p)}>{p}</Box>
                  ))}
                  {hasMore && <Box sx={{ color: '#385576', fontSize: '13px', px: '4px', userSelect: 'none', fontFamily: "'Open Sans', sans-serif" }}>...</Box>}
                  <Box sx={arrowSx(page === totalPages)} onClick={() => page < totalPages && setPage(page + 1)}>
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>
      <ConfirmReceptionDialog
        open={dialogOpen}
        onClose={() => !confirmLoading && setDialogOpen(false)}
        onConfirm={handleConfirmReception}
        loading={confirmLoading}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </Box>
  );
};

export default Orders;
